import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';

const URGENCY_CONFIG = {
  overdue: {
    label: 'Overdue',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    icon: ExclamationCircleIcon,
    iconColor: 'text-red-500',
  },
  'due-soon': {
    label: 'Due Soon',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    icon: ExclamationTriangleIcon,
    iconColor: 'text-amber-500',
  },
  recommended: {
    label: 'Recommended',
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    border: 'border-sky-200 dark:border-sky-800',
    badge: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
    icon: ClockIcon,
    iconColor: 'text-sky-500',
  },
};

export default function CareGapsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/premium/care-gaps');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load care gaps. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const overdueCount = data?.gaps?.filter(g => g.urgency === 'overdue').length || 0;
  const dueSoonCount = data?.gaps?.filter(g => g.urgency === 'due-soon').length || 0;

  if (user?.subscriptionStatus === 'starter') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <ShieldCheckIcon className="w-7 h-7 text-green-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Preventive Care Gaps requires Full Access</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-sm mx-auto">
          See which screenings and tests you may be missing based on your lab history and clinical guidelines. Available on the Full Access plan.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors"
        >
          <ShieldCheckIcon className="w-4 h-4" />
          Upgrade to Full Access — $25/mo
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <ShieldCheckIcon className="w-5 h-5 text-green-600" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Preventive Care Gaps</h1>
        </div>
        <p className="text-slate-500 dark:text-slate-400">
          Screenings and tests you may be missing — based on your lab history and clinical guidelines.
        </p>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
          <p className="text-sm">Analyzing your health history…</p>
        </div>
      )}

      {error && !loading && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl text-sm flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={load} className="ml-auto text-xs underline">Retry</button>
        </div>
      )}

      {data && !loading && (
        <div className="space-y-6">
          {/* Summary card */}
          <div className={`p-4 rounded-xl border flex items-start gap-3 ${overdueCount > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : dueSoonCount > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
            <ShieldCheckIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${overdueCount > 0 ? 'text-red-600' : dueSoonCount > 0 ? 'text-amber-600' : 'text-green-600'}`} />
            <p className={`text-sm font-medium ${overdueCount > 0 ? 'text-red-800 dark:text-red-200' : dueSoonCount > 0 ? 'text-amber-800 dark:text-amber-200' : 'text-green-800 dark:text-green-200'}`}>
              {data.summary}
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{overdueCount}</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Overdue</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{dueSoonCount}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Due Soon</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{data.upToDate?.length || 0}</p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Up to Date</p>
            </div>
          </div>

          {/* Gaps */}
          {data.gaps?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide mb-3">Action Needed</h2>
              <div className="space-y-3">
                {data.gaps.map((gap, i) => {
                  const cfg = URGENCY_CONFIG[gap.urgency] || URGENCY_CONFIG.recommended;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className={`${cfg.bg} ${cfg.border} border rounded-xl p-4`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-5 h-5 flex-shrink-0 ${cfg.iconColor}`} />
                          <p className="font-semibold text-slate-800 dark:text-white text-sm">{gap.screening}</p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.badge}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-2 ml-7">{gap.reason}</p>
                      {gap.lastDone && gap.lastDone !== 'never' && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 ml-7 mb-2">Last done: {gap.lastDone}</p>
                      )}
                      {gap.lastDone === 'never' && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 ml-7 mb-2">No record found</p>
                      )}
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-300 ml-7 bg-white dark:bg-white/10 bg-opacity-60 rounded-lg px-3 py-1.5 inline-block">
                        💬 {gap.action}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Up to date */}
          {data.upToDate?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">Up to Date</h2>
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-700">
                {data.upToDate.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <CheckBadgeIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <p className="text-sm text-slate-700 dark:text-slate-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Refresh */}
          <div className="flex justify-end">
            <button
              onClick={load}
              className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 border border-slate-200 dark:border-slate-600 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Refresh Analysis
            </button>
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
            Based on USPSTF and standard clinical guidelines. Consult your healthcare provider for personalized recommendations.
          </p>
        </div>
      )}
    </div>
  );
}
